/** The catalog grids' cells (activities, itineraries): a value or a state as a 14 px icon and
 *  words on one line (Icon-And-Words), worded by `words.ts`, so a list row says what the
 *  record's header and band say (One-Wording). */
import SyncIcon from '@mui/icons-material/Sync';
import { Box, type SvgIconProps } from '@mui/material';
import type { ComponentType, ReactNode } from 'react';

import { catalogSourceName } from './words';

const ELLIPSIS_SX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/** A muted 14 px icon, then the words, ellipsized to the cell; `title` holds the full
 *  sentence when the words are its short form. */
export function IconWords({
  icon: Icon,
  title,
  children,
}: {
  icon: ComponentType<SvgIconProps>;
  title?: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="span"
      title={title}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}
    >
      <Icon aria-hidden sx={{ fontSize: 14, flexShrink: 0, color: 'text.secondary' }} />
      <Box component="span" sx={ELLIPSIS_SX}>
        {children}
      </Box>
    </Box>
  );
}

/** The newest import's source as the record header's source chip says it: the sync icon and
 *  the source's name ("Imported from Intrepid" on hover); none: "Created in the console",
 *  muted. */
export function CatalogSourceCell({ name }: { name: string | null }) {
  const source = name?.trim();
  if (!source) {
    return (
      <Box component="span" sx={{ ...ELLIPSIS_SX, color: 'text.secondary' }}>
        {catalogSourceName(null)}
      </Box>
    );
  }
  return (
    <IconWords icon={SyncIcon} title={catalogSourceName(source)}>
      {source}
    </IconWords>
  );
}
